import { useState } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyL,
  BodyM,
  BodyS,
  HeadlineL,
} from '@sorare/core/src/atoms/typography';
import {
  SimpleSelect,
  SimpleSelectOption,
} from '@sorare/core/src/components/form/Form/SimpleSelect';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Department, Job } from '../types';
import { Section, StyledContainer, Title } from '../ui';

enum EmploymentType {
  FullTime = 'Permanent',
  PartTime = 'PartTime',
  Intern = 'Internship',
  Contract = 'Freelance',
  Temporary = 'Apprenticeship',
}

const BlackSection = styled(Section)`
  padding: var(--triple-unit) 0 var(--quadruple-unit);
`;
const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  @media ${mobileAndAbove} {
    flex-direction: row;
  }
  & > * {
    flex: 1;
  }
`;
const FormLabel = styled(BodyM)`
  margin-bottom: var(--unit);
`;

const DepartmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--triple-unit);
  @media ${mobileAndAbove} {
    flex-direction: row;
  }
`;
const DepartmentColumn = styled(Vertical).attrs({ gap: 3 })`
  flex: 1;
`;
const DepartmentTitle = styled(Title)`
  margin-left: var(--unit);
  margin-bottom: var(--double-unit);
  color: var(--c-brand-600);
`;
const JobContainer = styled(ButtonBase)`
  width: 100%;
  height: auto;
  display: block;
  text-align: left;
  padding: var(--unit) var(--double-unit);
  border-radius: 2em;

  &:hover {
    background-color: rgba(var(--c-rgb-brand-600), 0.1);
  }
  &:not(:last-of-type) {
    margin-bottom: var(--double-unit);
  }
`;

const Location = styled.span`
  font: var(--t-label-m);
  color: var(--c-nd-600);
`;

const JobDetails = styled(BodyS)`
  display: flex;
  align-items: center;
  gap: var(--intermediate-unit);
`;

type Props = {
  jobs: Job[];
};

export const JobBoard = ({ jobs }: Props) => {
  const { up: isLaptop } = useScreenSize('laptop');

  const [department, setDepartment] = useState<string>('');
  const [office, setOffice] = useState<string>('');
  const [jobType, setJobType] = useState<string>('');

  const filteredJobs = jobs.filter(
    job =>
      (!office || job.location === office) &&
      (!department || job.department === department) &&
      (!jobType || job.employmentType === jobType)
  );

  const officesOptions: SimpleSelectOption[] = [
    { label: 'All offices', value: '' },
    ...Array.from(new Set(jobs.map(job => job.location))).map(off => ({
      label: off,
      value: off,
    })),
  ];

  const departmentsOptions: SimpleSelectOption[] = [
    { label: 'All departments', value: '' },
    ...Array.from(new Set(jobs.map(job => job.department))).map(off => ({
      label: off,
      value: off,
    })),
  ];

  const jobTypeOptions: SimpleSelectOption[] = [
    { label: 'All job types', value: '' },
    ...Array.from(new Set(jobs.map(job => job.employmentType))).map(off => ({
      label: off,
      value: off,
    })),
  ];

  const columns: Department[][] = [[], [], []];
  const oneThirdTotalJobs = Math.floor(filteredJobs.length / columns.length);
  Object.entries(groupBy(filteredJobs, job => job.department)).forEach(
    ([dept, deptJobs = []]) => {
      for (let i = 0; i < columns.length; i += 1) {
        const depts: Department[] = columns[i];
        const sizeColumn = depts.reduce((acc, d) => acc + d.jobs.length, 0);
        if (
          sizeColumn + deptJobs.length <= oneThirdTotalJobs ||
          sizeColumn === 0
        ) {
          columns[i].push({ name: dept, jobs: deptJobs });
          break;
        } else if (i === columns.length - 1) {
          columns[0].push({ name: dept, jobs: deptJobs });
        }
      }
    }
  );

  if (jobs.length === 0) {
    return (
      <BlackSection center={!isLaptop}>
        <StyledContainer>
          <Title>Sorare Careers</Title>
          <HeadlineL as="h3">No vacancies</HeadlineL>
        </StyledContainer>
      </BlackSection>
    );
  }

  return (
    <>
      <BlackSection center={!isLaptop}>
        <StyledContainer>
          <Title>Sorare Careers</Title>
          <Form>
            <SimpleSelect
              value={department}
              onChange={e =>
                setDepartment((e.target as HTMLInputElement)?.value)
              }
              label={<FormLabel color="var(--c-black)">Department</FormLabel>}
              name="department"
              options={departmentsOptions}
            />
            <SimpleSelect
              value={office}
              onChange={e => setOffice((e.target as HTMLInputElement)?.value)}
              label={<FormLabel color="var(--c-black)">Office</FormLabel>}
              name="office"
              options={officesOptions}
            />
            <SimpleSelect
              value={jobType}
              onChange={e => setJobType((e.target as HTMLInputElement)?.value)}
              label={<FormLabel color="var(--c-black)">Job Type</FormLabel>}
              name="jobType"
              options={jobTypeOptions}
            />
          </Form>
        </StyledContainer>
      </BlackSection>
      {filteredJobs.length && (
        <Section $spacing>
          <StyledContainer>
            <DepartmentsContainer>
              {columns.map((departments, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <DepartmentColumn key={`column-${i}`}>
                  {departments.map(dept => (
                    <div key={dept.name}>
                      <DepartmentTitle>{dept.name}</DepartmentTitle>
                      {dept.jobs.map(job => (
                        <JobContainer key={job.id} to={`?jid=${job.id}`}>
                          <BodyL color="var(--c-nd-800)">{job.title}</BodyL>
                          <JobDetails>
                            {
                              EmploymentType[
                                job.employmentType as keyof typeof EmploymentType
                              ]
                            }
                            <Location>{job.location}</Location>
                          </JobDetails>
                        </JobContainer>
                      ))}
                    </div>
                  ))}
                </DepartmentColumn>
              ))}
            </DepartmentsContainer>
          </StyledContainer>
        </Section>
      )}
    </>
  );
};
